import { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, HelpCircle, User, Users, Github } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { LanguageSwitcher } from '@/components/language-switcher';
import { removeToken, getUser, getToken, authApi } from '@/lib/api/client';

interface UserInfo {
  id: number;
  username: string;
  githubId?: string | null;
  role?: 'ADMIN' | 'USER';
}

export function MainLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const token = getToken();
  const user = useMemo(() => getUser(), [token]);

  useEffect(() => {
    if (user) {
      // Fetch complete user info including GitHub binding status
      authApi.getCurrentUser()
        .then((data) => setUserInfo(data))
        .catch((err) => console.error('Failed to fetch user info', err));
    } else {
      setUserInfo(null);
    }
  }, [token, user]);

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  const handleUnbindGithub = async () => {
    if (!confirm(t('auth.confirmUnbind'))) {
      return;
    }

    try {
      await authApi.unbindGithub();
      // Refresh user info
      const data = await authApi.getCurrentUser();
      setUserInfo(data);
      alert(t('auth.unbindSuccess'));
    } catch (error) {
      console.error('Failed to unbind GitHub', error);
      alert(t('auth.unbindFailed'));
    }
  };

  const handleBindGithub = () => {
    const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;
    window.location.href = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&state=bind`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 relative flex flex-col">
      <header className="absolute top-0 left-0 w-full p-4 flex justify-between items-center z-50">
        {/* Left Side: Language Switcher */}
        <div>
          <LanguageSwitcher align="start" />
        </div>

        {/* Right Side: Navigation & User Profile */}
        <div className="flex gap-2 items-center">
          {user ? (
            <div
              onMouseEnter={() => setIsMenuOpen(true)}
              onMouseLeave={() => setIsMenuOpen(false)}
            >
              <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 px-3 py-2 text-slate-700 bg-white/50 rounded-full border border-slate-200 hover:bg-white/80"
                  >
                    <User className="h-4 w-4" />
                    <span className="text-sm font-medium">{user.username}</span>
                    {user.role && (
                      <Badge
                        variant="secondary"
                        className={`text-[10px] px-1.5 py-0 h-4 border-none text-white bg-gradient-to-r ${
                          user.role === 'ADMIN'
                            ? 'from-zinc-900 via-amber-200 to-zinc-900 text-amber-200 font-bold border border-amber-500/30'
                            : 'from-blue-500 via-indigo-500 to-cyan-500'
                        } bg-[length:200%_auto] animate-shimmer-once shadow-sm`}
                      >
                        {user.role}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel>{t('user.account', 'My Account')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-muted-foreground flex justify-between items-center">
                   
                    {user.role && (
                      <Badge
                        variant="secondary"
                        className={`text-[10px] px-1.2 py-0 h-3.5 border-none text-white bg-gradient-to-r ${
                          user.role === 'ADMIN'
                            ? 'from-zinc-900 via-amber-200 to-zinc-900 text-amber-200 font-bold border border-amber-500/30'
                            : 'from-blue-500 via-indigo-500 to-cyan-500'
                        } bg-[length:200%_auto] animate-shimmer-once`}
                      >
                        {user.role}
                      </Badge>
                    )}
                  </DropdownMenuItem>

                  {user?.role === 'ADMIN' && (
                    <DropdownMenuItem asChild>
                      <Link to="/users" className="flex items-center cursor-pointer">
                        <Users className="mr-2 h-4 w-4" />
                        <span>{t('auth.userManagement')}</span>
                      </Link>
                    </DropdownMenuItem>
                  )}

                  {userInfo?.githubId ? (
                    <DropdownMenuItem
                      onClick={handleUnbindGithub}
                      className="cursor-pointer"
                    >
                      <Github className="mr-2 h-4 w-4" />
                      <span>{t('auth.unbindGithub')}</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      onClick={handleBindGithub}
                      className="cursor-pointer"
                    >
                      <Github className="mr-2 h-4 w-4" />
                      <span>{t('auth.connectGithub')}</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />

                   <DropdownMenuItem asChild>
                    <Link to="/guide" className="flex items-center cursor-pointer">
                      <HelpCircle className="mr-2 h-4 w-4" />
                      <span>{t('nav.guide')}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600 cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('auth.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <Link to="/login">
              <Button variant="ghost">{t('auth.login', 'Login')}</Button>
            </Link>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center border-t border-slate-200 mt-auto">
        <div className="max-w-4xl mx-auto mb-4">
          <p className="text-xs text-slate-400 leading-relaxed">
            {t('index.disclaimer')}
          </p>
        </div>
        <p className="text-slate-400 text-sm">
          Designed by <span className="font-medium text-slate-500">Alan</span>, Thanks <span className="font-medium text-slate-500">Vercel</span>
        </p>
      </footer>
    </div>
  );
}
